import 'whatwg-fetch'
import 'element-matches-polyfill'

import {
  ITag,
  IFormTag,
  IEventTag,
  IEventTagV2,
  ITrackFormTag,
  FormTagV2,
  SupportedDomEvents,
  IBinding,
  ISelectorBinding,
  IStaticBinding,
  UrlRule,
  RuleType,
  Action,
  ActionType,
  SchemaV2Tag
} from './types'

const bubbleEvents = ['submit']

function isEventTag(tag: ITag): tag is IEventTag {
  return (
    (tag as IEventTag).taggingMethod === 'event' ||
    (tag as IEventTag).taggingMethod === undefined
  )
}

function isEventTagV2(tag: ITag): tag is IEventTagV2 {
  return isSchemaV2(tag) && tag.handler === 'click'
}

function isTrackFormTag(tag: ITag): tag is ITrackFormTag {
  return (
    (tag as IFormTag).formAction === 'track' &&
    (tag as IFormTag).taggingMethod === 'form'
  )
}

// TECH-DEBT [VT-815] [VT-816]:
// This check will become the default once we backfill specs with the `actions` field
function isFormTagV2(tag: ITag): tag is FormTagV2 {
  return isSchemaV2(tag) && tag.handler === 'submit'
}

// TECH-DEBT [VT-815] [VT-816]
function isSchemaV2(tag: ITag): tag is SchemaV2Tag {
  return (tag as SchemaV2Tag).actions && (tag as SchemaV2Tag).actions.length > 0
}

function isTrackAction(type: ActionType): boolean {
  return type === 'track'
}

function isIdentifyAction(type: ActionType): boolean {
  return type === 'identify'
}

type BindingToPropertyMap = { [key: string]: string }
type EventToTagMap = { [k in SupportedDomEvents]: ITag[] }
type EventToListenerMap = { [k in SupportedDomEvents]: EventListener }

const getBucketPath = (workspaceId: string, sourceId: string) =>
  `https://d1wnclalxop6x4.cloudfront.net/instrumentation-spec-${workspaceId}-${sourceId}.json`

export default class Tracktor {
  private initialized: boolean
  private listenerMap: EventToListenerMap
  private workspaceId: string
  private sourceId: string
  private documentNode: Document
  private instrumentationSpec: ITag[]
  static instance: Tracktor
  constructor(
    workspaceId: string,
    sourceId: string,
    instrumentationSpec: ITag[],
    documentNode: Document
  ) {
    if (!workspaceId || !sourceId) {
      throw new Error('workspaceId and sourceId must be specified')
    }
    if (Tracktor.instance) {
      return Tracktor.instance
    }
    Tracktor.instance = this
    this.workspaceId = workspaceId
    this.sourceId = sourceId
    this.instrumentationSpec = instrumentationSpec
    this.documentNode = documentNode
  }

  public start(): Promise<void> {
    return Promise.resolve()
      .then(this.getInstrumentationSpec)
      .then(this.load)
  }

  public restart(specs: ITag[]): void {
    this.removeListeners()
    this.load(specs)
  }

  public isInitialized() {
    return this.initialized
  }

  private load = (specs: ITag[]) => {
    this.setListenerMap(this.toSpecMapByHandlerKey(specs))
    // TODO: Guard against this being invoked more than once
    this.initialized = true
    this.installListeners()
  }

  private getInstrumentationSpec = () => {
    if (this.instrumentationSpec) {
      return Promise.resolve(this.instrumentationSpec)
    }

    return fetch(getBucketPath(this.workspaceId, this.sourceId), {
      headers: {
        'Cache-Control': 'no-cache'
      }
    }).then<ITag[]>(res => (res.ok ? res.json() : []))
  }

  private toSpecMapByHandlerKey(specs: ITag[]) {
    return specs.reduce(
      (res, spec) => ({
        ...res,
        [spec.handler]: [...(res[spec.handler] || []), spec]
      }),
      {} as EventToTagMap
    )
  }

  /**
   * Evaluates whether the event should fire based on whether the current url follows
   * the tag's url rules
   */
  private testUrl(urlRules: UrlRule[]): boolean {
    if (!urlRules) return true

    const url = window.location.href
    const ruleEvaluators: {
      [ruleType in RuleType]: (pattern: string) => boolean
    } = {
      is: pattern => pattern === url,
      isNot: pattern => pattern !== url,
      startsWith: pattern => url.startsWith(pattern),
      endsWith: pattern => url.endsWith(pattern),
      contains: pattern => url.includes(pattern),
      containsNot: pattern => !url.includes(pattern),
      regex: pattern => RegExp(pattern).test(url)
    }
    for (const urlRule of urlRules) {
      const { ruleType, pattern } = urlRule
      if (!ruleEvaluators[ruleType](pattern)) {
        return false
      }
    }
    return true
  }

  /**
   * Expands the "radius" of the selector by including ancestor elements up to a height defined
   * by maxAncestorHeight
   */
  private findMatch(
    tag: ITag,
    childElement: Element,
    maxAncestorHeight: number = 8
  ) {
    let curEl = childElement
    let curHeight = 0
    while (curEl && curHeight < maxAncestorHeight) {
      if (curEl.matches(tag.selector)) {
        return curEl
      }
      curEl = curEl.parentElement!
      curHeight++
    }
    return undefined
  }

  /**
   * For each event that a user has declared in their instrumentation spec,
   * this function will set an internal map of event names to handlers
   */
  private setListenerMap = (eventToTagMap: EventToTagMap) => {
    this.listenerMap = Object.entries(eventToTagMap).reduce(
      (listenerMap, [event, tags]) => {
        return {
          ...listenerMap,
          [event]: e => {
            const matchedTags = tags.filter(
              (s: ITag) =>
                this.testUrl(s.urlRules) && !!this.findMatch(s, e.target)
            )

            if (matchedTags.length === 0) {
              return
            }

            for (const tag of matchedTags) {
              if (isEventTagV2(tag)) {
                for (const action of tag.actions) {
                  this.handleEventTag(
                    tag,
                    e.target as Element,
                    action.event,
                    action.bindings
                  )
                }
              } else if (isEventTag(tag)) {
                this.handleEventTag(
                  tag,
                  e.target as Element,
                  tag.event,
                  tag.bindings
                )
              } else if (isFormTagV2(tag)) {
                for (const action of tag.actions) {
                  if (isTrackAction(action.type)) {
                    this.handleTrackFormTag(tag, action.event, action.bindings)
                  } else if (isIdentifyAction(action.type)) {
                    this.handleIdentifyFormTag(tag, action.bindings)
                  }
                }
              } else if (isTrackFormTag(tag)) {
                this.handleTrackFormTag(tag, tag.event, tag.bindings)
              }
            }

            return
          }
        }
      },
      {} as EventToListenerMap
    )
  }

  private handleEventTag(
    tag: IEventTag,
    target: Element,
    event: string,
    bindings: IBinding[]
  ) {
    // infer event generalization based on selectorMatches
    const selectorMatches = this.documentNode.querySelectorAll(tag.selector)
    const eventElement = this.findMatch(tag, target)
    const { dynamicBindings, staticBindings } = this.splitBindings(bindings)
    const dynamicProperties = dynamicBindings.reduce((props, binding) => {
      let propertyElement
      // Check for dynamic property within the eventElement itself. Doing this
      // first ensures generalized dynamic properties are found contained within
      // element, and also ensures that generalized selectors which happen to only
      // select one element still pick up their properties.
      if (eventElement) {
        propertyElement = eventElement.querySelector<HTMLInputElement>(
          binding.selector
        )
      }

      // If the property is not found within the element, and the event is not
      // generalized, search the dom directly
      if (!propertyElement && selectorMatches.length < 2) {
        propertyElement = this.documentNode.querySelector<HTMLInputElement>(
          binding.selector
        )
      }

      return {
        ...props,
        [binding.field]:
          propertyElement &&
          (propertyElement.value || propertyElement.innerText)
      }
    }, {})

    const staticProperties = staticBindings.reduce(
      (traits, binding: IStaticBinding) => {
        return {
          ...traits,
          [binding.field]: binding.value
        }
      },
      {}
    )

    const properties = { ...staticProperties, ...dynamicProperties }

    window.analytics.track(event, properties, {
      context: {
        visual_tagger: true
      }
    })
  }

  private getBindingValuesFromForm(staticBindings: IStaticBinding[], dynamicBindings: ISelectorBinding[], formEl: HTMLFormElement): BindingToPropertyMap {
    const dynamicProperties = dynamicBindings.reduce((props, binding) => {
      const fieldValue = formEl.querySelector<HTMLInputElement>(
        binding.selector
      )

      if (!fieldValue) {
        // The element/field does not exist in the form. The selector may instead refer
        // to a dynamic property added by the user outside the form itself
        const element: HTMLInputElement | null = this.documentNode.querySelector<
          HTMLInputElement
        >(binding.selector)

        return {
          ...props,
          [binding.field]: element && (element.value || element.innerText)
        }
      }

      return {
        ...props,
        [binding.field]: fieldValue && fieldValue.value
      }
    }, {})

    const staticProperties = staticBindings.reduce(
      (traits, binding: IStaticBinding) => {
        return {
          ...traits,
          [binding.field]: binding.value
        }
      },
      {}
    )

    return { ...staticProperties, ...dynamicProperties }
  }

  private handleTrackFormTag(
    tag: ITrackFormTag,
    event: string,
    bindings: IBinding[]
  ) {
    const formEl = this.documentNode.querySelector<HTMLFormElement>(
      tag.selector
    )

    if (!formEl) {
      console.error(
        `form tagged at ${tag.selector} cannot be found in the current page `
      )
      return
    }

    const { dynamicBindings, staticBindings } = this.splitBindings(bindings)

    const properties = this.getBindingValuesFromForm(staticBindings, dynamicBindings, formEl)

    window.analytics.track(event, properties, {
      context: {
        visual_tagger: true
      }
    })
  }

  private handleIdentifyFormTag(
    tag: ITrackFormTag,
    bindings: IBinding[]
  ) {
    const formEl = this.documentNode.querySelector<HTMLFormElement>(
      tag.selector
    )

    if (!formEl) {
      console.error(
        `form tagged at ${tag.selector} cannot be found in the current page `
      )
      return
    }

    const { dynamicBindings, staticBindings } = this.splitBindings(bindings)

    const traits = this.getBindingValuesFromForm(staticBindings, dynamicBindings, formEl)

    window.analytics.identify(traits, {
      context: {
        visual_tagger: true
      }
    })
  }

  private splitBindings(bindings: IBinding[]) {
    const dynamicBindings = bindings.filter(
      (binding: IBinding) => binding.type && binding.type !== 'static'
    ) as ISelectorBinding[]
    const staticBindings = bindings.filter(
      (binding: IBinding) => binding.type && binding.type === 'static'
    ) as IStaticBinding[]

    return {
      staticBindings,
      dynamicBindings
    }
  }

  private installListeners() {
    Object.entries(this.listenerMap).forEach(([event, listener]) => {
      const useCapture = !bubbleEvents.includes(event)
      this.documentNode.addEventListener(event, listener, useCapture)
    })
  }

  private removeListeners() {
    Object.entries(this.listenerMap).forEach(([event, listener]) => {
      const useCapture = !bubbleEvents.includes(event)
      this.documentNode.removeEventListener(event, listener, useCapture)
    })
  }
}
